import type { ScheduleSlotPayload } from '../types/api-responses';

/**
 * AB#2011 (CR-2026-07-18) — resolve a staff member's working window for a
 * given branch + calendar date from their per-branch weekly schedule
 * (GET /salon-owner/staff/{id}/schedule), so the absence editor can show the
 * hours and softly warn when an intra-day break falls outside them.
 *
 * We deliberately only assert two positive states the client can KNOW from the
 * schedule rows alone:
 *   - `working` — an explicit available slot exists for (branch, weekday).
 *   - `off`     — the staff has rows for this branch but not an available one
 *                 on this weekday.
 * When the staff has NO rows for the branch at all we return `unknown`: that is
 * the backend's "implicit-all" case (staff follows the branch's open hours),
 * which the FE can't resolve without branch hours — so we stay neutral and
 * never warn, to avoid false alarms on fresh salons. The authoritative
 * out-of-hours check (incl. implicit-all + branch hours) is the deferred
 * backend warning; see AB#2011 AC-4.
 */
export type WorkingWindow =
    | { kind: 'working'; start: string; end: string } // "HH:MM"
    | { kind: 'off' }
    | { kind: 'unknown' };

/** "09:00:00" | "09:00" -> "09:00". */
const hhmm = (t: string): string => (t ?? '').slice(0, 5);

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * The BE serializes DayOfWeek as the enum NAME ("Friday"), though the FE type
 * calls it numeric (0..6). Coerce both wire forms to 0..6 (0=Sun..6=Sat) so the
 * day match doesn't silently miss and fall through to "off". Mirrors
 * `normalizeDayOfWeek` in services/api.ts (AB#2002).
 */
const toDayNumber = (d: number | string): number => {
    if (typeof d === 'number') return d;
    const i = DAY_NAMES.indexOf(String(d).trim().toLowerCase());
    return i >= 0 ? i : Number(d);
};

/** Local day-of-week (0=Sun..6=Sat) for a "yyyy-MM-dd" string, timezone-safe. */
export const dayOfWeekOf = (isoDate: string): number => {
    const [y, m, d] = isoDate.split('-').map(Number);
    if (!y || !m || !d) return new Date(isoDate).getDay();
    return new Date(y, m - 1, d).getDay();
};

export function resolveWorkingWindow(
    slots: ScheduleSlotPayload[] | undefined,
    branchId: number | null,
    isoDate: string,
): WorkingWindow {
    if (!slots || slots.length === 0) return { kind: 'unknown' };

    // Rows scoped to this branch. On single-branch businesses the server may
    // store branchId as null, so a null branchId matches null-branch rows.
    const forBranch = slots.filter(s => s.branchId === branchId || s.branchId == null);
    if (forBranch.length === 0) return { kind: 'unknown' };

    const dow = dayOfWeekOf(isoDate);
    const today = forBranch.filter(s => toDayNumber(s.dayOfWeek) === dow);
    const open = today.find(s => s.isAvailable);
    if (open) return { kind: 'working', start: hhmm(open.startTime), end: hhmm(open.endTime) };

    // Has rows for the branch, but none available this weekday -> off.
    return { kind: 'off' };
}

/**
 * True when an intra-day window [start,end] (each "HH:MM") falls outside the
 * resolved working window, or the staff is off that day. `unknown` never warns.
 * Zero-padded 24h "HH:MM" strings compare correctly lexicographically.
 */
export function isOutsideWorkingHours(
    window: WorkingWindow,
    startTime: string,
    endTime: string,
): boolean {
    if (window.kind === 'off') return true;
    if (window.kind === 'working') return startTime < window.start || endTime > window.end;
    return false; // unknown
}

/**
 * AB#2012 — client-side overlap detection (mirrors the authoritative BE check
 * in SetStaffAbsenceCommand) so the owner sees a localized block before the
 * round-trip. Whole-day involvement on overlapping dates clashes; two intra-day
 * rows clash only when their "HH:MM" time windows intersect on a shared date.
 */
export interface AbsenceLike {
    id: number;
    startDate: string;      // "yyyy-MM-dd"
    endDate: string;
    startTime: string | null; // "HH:MM" (or "HH:MM:SS") — null for whole-day
    endTime: string | null;
    /** Enum name ("Break"/"Vacation"/…) — used to name the conflict in the error. */
    absenceType?: string;
}

/**
 * Returns the first existing absence that clashes with the proposed one, or
 * null if there's no conflict — so the caller can name the offender in the
 * error (AB#2013b). Same interval logic as the BE `FindOverlapKindAsync`.
 *
 * CR-2026-07-19 (AB#2018) — clash requires the SAME granularity. A whole-day
 * leave (Sick/Vacation) and an intra-day break (Break/Personal/Emergency) are
 * different granularities and do NOT clash: the leave supersedes the break
 * inside its range (the break is kept, unused during the leave), and adding a
 * break onto a leave day is likewise not a conflict. Same principle as AB#2013.
 * Only two whole-day leaves overlapping in dates, or two intra-day windows whose
 * times intersect on a shared date, clash.
 */
export function findOverlappingAbsence(
    existing: AbsenceLike[] | undefined,
    selfId: number | null,
    newStartDate: string,
    newEndDate: string,
    newStartTime: string | null, // "HH:MM" or null (whole-day)
    newEndTime: string | null,
): AbsenceLike | null {
    if (!existing || existing.length === 0) return null;
    const newWhole = !newStartTime || !newEndTime;
    for (const e of existing) {
        if (selfId != null && e.id === selfId) continue;
        // date ranges must intersect
        if (newStartDate > e.endDate || e.startDate > newEndDate) continue;
        const existWhole = !e.startTime || !e.endTime;
        // mixed granularity never clashes
        if (newWhole !== existWhole) continue;
        // both whole-day: overlapping dates already established above
        if (newWhole) return e;
        // both intra-day: clash only when the time windows intersect
        if (newStartTime! < hhmm(e.endTime!) && hhmm(e.startTime!) < newEndTime!) return e;
    }
    return null;
}

const ABSENCE_TYPE_I18N: Record<string, string> = {
    Break: 'staff.absence.type.break',
    SickLeave: 'staff.absence.type.sick',
    Vacation: 'staff.absence.type.vacation',
    PersonalTime: 'staff.absence.type.personal',
    Emergency: 'staff.absence.type.emergency',
};

/**
 * Human-readable "{type} · {when}" for a conflicting absence, e.g.
 * "Vacation · 2026-07-24 → 2026-07-27" or "Break · 2026-07-24 ⁨13:00–14:00⁩".
 * Times are LTR-isolated (FSI/PDI) for RTL. Dates stay numeric (locale-neutral).
 */
export function describeAbsence(a: AbsenceLike, t: (k: string) => string): string {
    const key = a.absenceType ? ABSENCE_TYPE_I18N[a.absenceType] : undefined;
    const type = key ? t(key) : (a.absenceType ?? '');
    const whole = !a.startTime || !a.endTime;
    // A multi-day range carries a "→" plus two numeric dates — an LTR run that
    // must be bidi-isolated (FSI…PDI) so the Arabic renderer doesn't reorder the
    // dates or flip the arrow (AB#2018 committee finding).
    const dates = a.startDate === a.endDate ? a.startDate : `⁨${a.startDate} → ${a.endDate}⁩`;
    const when = whole ? dates : `${a.startDate} ⁨${hhmm(a.startTime!)}–${hhmm(a.endTime!)}⁩`;
    return type ? `${type} · ${when}` : when;
}
